import { useCallback, useEffect, useRef, useState } from 'react';

// Detect when cotty is already open in another tab/window of the SAME browser
// (BroadcastChannel is same-origin, same-browser). Two live instances would run
// duplicate terminal WebSockets and race the workspace auto-save, so the second
// window is parked behind a notice until the user explicitly takes over here.
//
// Protocol (every message carries the sender's `id`):
//   query  – "is anyone the active window?"        (sent on startup / recovery)
//   active – reply to `query` from the active one  → asker becomes a duplicate
//   claim  – "I am the active window now"          → everyone else steps down
//   bye    – "I'm closing"                         → duplicates try to take over

const CHANNEL = 'cotty-window-guard';
type Msg = { t: 'query' | 'active' | 'claim' | 'bye'; id: string };

export function useDuplicateWindow(): { duplicate: boolean; takeOver: () => void } {
    const [duplicate, setDuplicate] = useState(false);
    const bcRef = useRef<BroadcastChannel | null>(null);
    const idRef = useRef('');
    const roleRef = useRef<'pending' | 'active' | 'duplicate'>('pending');
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Older browsers without BroadcastChannel simply skip the guard.
        if (typeof BroadcastChannel === 'undefined') return;

        const id =
            (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
            String(Math.random());
        idRef.current = id;
        const bc = new BroadcastChannel(CHANNEL);
        bcRef.current = bc;

        const post = (t: Msg['t']) => bc.postMessage({ t, id } satisfies Msg);
        const clearTimer = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
        const becomeActive = () => {
            clearTimer();
            roleRef.current = 'active';
            setDuplicate(false);
        };
        const becomeDuplicate = () => {
            clearTimer();
            roleRef.current = 'duplicate';
            setDuplicate(true);
        };

        // Ask who's active; if nobody answers within a jittered window, claim it.
        // The jitter keeps two windows opened at once from both claiming.
        const probe = () => {
            clearTimer();
            roleRef.current = 'pending';
            post('query');
            timerRef.current = setTimeout(
                () => {
                    if (roleRef.current === 'pending') {
                        becomeActive();
                        post('claim');
                    }
                },
                180 + Math.floor(Math.random() * 160),
            );
        };

        bc.onmessage = (e: MessageEvent<Msg>) => {
            const m = e.data;
            if (!m || m.id === id) return;
            switch (m.t) {
                case 'query':
                    if (roleRef.current === 'active') post('active');
                    break;
                case 'active': // an active window already exists
                case 'claim': // another window (re)claimed activity
                    becomeDuplicate();
                    break;
                case 'bye':
                    // The active window closed — if we're parked, try to take over.
                    if (roleRef.current === 'duplicate') probe();
                    break;
            }
        };

        const onHide = () => {
            try {
                post('bye');
            } catch {
                /* channel may already be closing */
            }
        };
        window.addEventListener('pagehide', onHide);

        probe();

        return () => {
            clearTimer();
            window.removeEventListener('pagehide', onHide);
            onHide();
            bc.close();
            bcRef.current = null;
        };
    }, []);

    // User chose to run here: claim activity (parks the other windows) and resume.
    const takeOver = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        roleRef.current = 'active';
        setDuplicate(false);
        bcRef.current?.postMessage({ t: 'claim', id: idRef.current } satisfies Msg);
    }, []);

    return { duplicate, takeOver };
}
